// [2026-09-02] ADDED — the signed-in customer's credit balance and ledger.
//
// Reads GET /me/credits (platform.credits), authenticated by the account session. Credits belong to
// the COMPANY, not to any API key, so rotating a key never touches this balance. The ledger is the
// audit trail: a grant from an InCruiter operator shows as a positive entry, and every proctoring
// run that consumes credits shows as a negative one — this is where a customer watches their balance
// go down as they use the product.

import { platform } from '../../api.js'
// [2026-09-05] CHANGED — this screen now uses the platform primitives and the shared DataTable,
// so the ledger gets the same search / filter / sort / pagination furniture as every other table.
// OLD: import { Card, Empty, Notice, Skeleton, Tile, num, when, useAsync, downloadCsv } from '../../ui.jsx'
//      import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
//        from '../../components/ui/table.jsx'
// The shared modules are UNCHANGED and still exported; this file just stopped importing them.
import { credits as fmtCredits, reasonText, num, when, useAsync } from '../../ui.jsx'
import { CreditsChart } from '../../charts.jsx'
import { PfCard, PfNotice, PfSkeleton, PfTile } from '../../components/platform/ui.jsx'
// [2026-09-25] CHANGED — CalendarIcon added, for the "In Progress" tile copied from the Dashboard.
// OLD: import { CardIcon, TrendingUpIcon, ZapIcon } from '../../components/platform/icons.jsx'
import { CalendarIcon, CardIcon, TrendingUpIcon, ZapIcon } from '../../components/platform/icons.jsx'
import DataTable from './DataTable.jsx'
import { makeClientPager } from '../../components/platform/pagination.jsx'
// [2026-09-06] ADDED — the per-service split of what those credits were spent on. See the header
// of the component for why call volume and error rate belong on the billing screen and not only in
// Analytics.
import ServiceBreakdown from './ServiceBreakdown.jsx'

// A ledger row's day, for the per-day spend chart. created_at is an ISO string (app/core/credits.py).
const dayOf = (createdAt) => (typeof createdAt === 'string' ? createdAt.slice(0, 10) : '')

// Aggregate the SPEND (negative entries) per day, so the chart shows the balance being consumed.
function spendByDay(ledger) {
  const byDay = new Map()
  for (const row of ledger || []) {
    const spent = row.amount_credits < 0 ? -row.amount_credits : 0
    if (!spent) continue
    const day = dayOf(row.created_at)
    if (!day) continue
    byDay.set(day, (byDay.get(day) || 0) + spent)
  }
  return [...byDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([date, credits]) => ({ date, credits }))
}

// [2026-09-11] ADDED — every day in the window, including the ones with no spend.
//
// THE BUG THIS FIXES. spendByDay above emits a point ONLY for a day that had a debit. On an account
// that spent on two days out of thirty, "last 30 days" drew a TWO-POINT line — 9 Sept on the left,
// 10 Sept on the right, and a straight diagonal between them. That is not a sparse chart, it is a
// misleading one: a straight rising line reads as steady accumulation, when what actually happened
// was nothing, nothing, nothing, then two busy days. Reported by Rujal, who could see two dates on
// an axis labelled thirty days and correctly did not believe it.
//
// Recharts joins the points it is given; it cannot know a day is missing rather than zero. So the
// series has to be dense before it gets there.
//
// ZERO-FILLED, NOT INTERPOLATED. A day with no debit really did consume zero credits — this is not
// a guess standing in for absent data, it is the value. That is what makes filling honest here and
// what would make it wrong on, say, a latency chart, where "no calls" means UNKNOWN and must stay a
// gap.
//
// ⚠️ ONE LIMIT THIS DOES NOT FIX, AND IT IS THE SERVER'S: /me/credits returns at most 500 ledger
// rows (app/modules/me/route.py), newest first, and this screen asks for 200. A busy account whose
// 200 most recent movements do not reach back 30 days will show real zeros for the older days
// because those rows were never sent — the chart cannot distinguish "no spend" from "beyond the
// page". Fixing that properly means a server-side per-day aggregate, like sessions_series, rather
// than summing a truncated ledger in the browser. Flagged rather than papered over.
function denseDays(points, days) {
  const have = new Map(points.map((p) => [p.date, p.credits]))
  const out = []
  // Walk back from today so the last point is always "today", even if nothing was spent today —
  // otherwise the line stops mid-chart and looks like the data is stale.
  const today = new Date()
  for (let back = days - 1; back >= 0; back -= 1) {
    const d = new Date(today.getTime() - back * 86400000)
    const key = d.toISOString().slice(0, 10)
    out.push({ date: key, credits: have.get(key) || 0 })
  }
  return out
}

// [2026-09-02] ADDED — the cut-off date for the chart window, as a YYYY-MM-DD string.
//
// String comparison rather than Date arithmetic: `created_at` is already an ISO string and the day
// key is its first ten characters, so comparing the two as text is exact and needs no timezone
// decision. Parsing to Date and back would introduce one — and the ledger day would then shift for
// anyone east of UTC.
function cutoffDay(days) {
  const from = new Date(Date.now() - days * 86400000)
  return from.toISOString().slice(0, 10)
}

// [2026-09-07] ADDED — the switch that takes "Credit utilisation by service" off this screen.
//
// It broke the spend into SDK vs Proctoring and showed call volume and error rate beside each. We
// sell one product — proctoring plus the SDK, on one balance — so splitting the bill into two
// internal services invited exactly the question the packaging is meant to avoid ("am I paying
// twice?"). The same reasoning already retired the split sessions graph on the dashboard.
//
// ONE flag, not two edits, because the card has a COST: it fires a second request to /me/analytics.
// Hiding only the JSX would leave that request running forever on a screen that no longer renders
// its result. The flag gates the fetch and the render together, so turning the card back on turns
// its data back on with it.
const SHOW_SERVICE_BREAKDOWN = false

// [2026-09-02] CHANGED — the screen now HONOURS the `days` prop. OLD signature:
//   export default function PlatformCredits({ onError }) {
//   const { data, error, loading } = useAsync(() => platform.credits(200), [])
//
// PlatformApp has always passed `days` here (PlatformApp.jsx:169) and `credits` is not in
// RANGELESS_VIEWS, so the range picker was rendered above this screen and did nothing at all — the
// worst kind of control, because moving it looks like it worked. GET /me/credits takes only a
// `limit` (app/modules/me/route.py:181), so the window is applied to the SPEND CHART, which is
// derived client-side from the ledger anyway. The ledger table below stays complete and says so:
// truncating someone's audit trail to match a chart control would be the wrong half to cut.
export default function PlatformCredits({ onError, days = 30 }) {
  const { data, error, loading } = useAsync(() => platform.credits(200), [])

  // [2026-09-06] ADDED — a SECOND request, for the traffic behind the spend.
  //
  // WHY NOT FOLDED INTO /me/credits. That endpoint is the ledger: a durable, exact record of money.
  // The calls-and-errors figures come from the usage rollup, which is explicitly best-effort and
  // fails open (app/core/usage.py). Widening the billing endpoint to also carry lossy analytics
  // would put the two behind one failure and one cache lifetime, and the first time metering hiccuped
  // it would take the balance down with it.
  //
  // IT DELIBERATELY DOES NOT CALL onError. A metering outage must not raise a red banner on a screen
  // whose primary content — the balance and the ledger — loaded perfectly. The breakdown card
  // degrades its own traffic figures to "—" and says so inline; see `failed` there.
  //
  // KEYED ON `days`, so moving the range picker actually re-fetches. The chart below is derived
  // client-side from the ledger and so needs no refetch, which is why the existing call has an empty
  // dependency list — copying that here would have made the picker a no-op for these figures, the
  // exact bug the comment above this component records being fixed once already.
  // [2026-09-07] CHANGED — short-circuited while SHOW_SERVICE_BREAKDOWN is false, so the screen no
  // longer pays for data nothing renders. The hook itself still runs unconditionally — a hook behind
  // an `if` is the one change here that would actually break React.
  // OLD: const traffic = useAsync(() => platform.analytics(days).catch(() => null), [days])
  const traffic = useAsync(
    () => (SHOW_SERVICE_BREAKDOWN ? platform.analytics(days).catch(() => null) : Promise.resolve(null)),
    [days],
  )

  if (error) { onError?.(error); return <PfNotice kind="error">{error.message}</PfNotice> }
  if (loading || !data) return <PfSkeleton height={280} />

  const s = data.summary || {}
  const ledger = data.ledger || []
  const since = cutoffDay(days)
  // [2026-09-11] CHANGED — the series is now dense across the whole window.
  // OLD: const chart = spendByDay(ledger).filter((point) => point.date >= since)
  // See denseDays above: the filtered version drew a two-point line across a thirty-day axis.
  const chart = denseDays(spendByDay(ledger).filter((point) => point.date >= since), days)

  // [2026-09-05] ADDED — the ledger as a real table.
  //
  // GET /me/credits returns the whole ledger in ONE response (`limit`, capped at 500 server-side —
  // app/modules/me/route.py:292-300), so the browser already holds every movement. That is what
  // makes client-side search, filter, sort AND pagination honest here: each one acts on the
  // complete set, not on a visible slice. The two server-paginated tables in this app deliberately
  // have no sort for the opposite reason.
  const fetchPage = makeClientPager(ledger, {
    // [2026-09-05] ADDED — lets the shared filter panel's date range and Order By work here too,
    // against the whole ledger the browser already holds.
    dateOf: (row) => row.created_at,
    nameOf: (row) => row.type,
    searchIn: (row) => `${row.type} ${row.reason || ''} ${row.amount_credits}`,
    filterFns: {
      direction: (value) => (row) => (value === 'in'
        ? (row.amount_credits || 0) >= 0
        : (row.amount_credits || 0) < 0),
      type: (value) => (row) => row.type === value,
    },
  })

  // The distinct entry types actually present, so the filter never offers one that would return
  // nothing. Built from the data rather than from a hardcoded list of the six the backend defines,
  // which would drift the day a seventh is added.
  const types = [...new Set(ledger.map((r) => r.type).filter(Boolean))].sort()

  const columns = [
    { key: 'created_at', label: 'When', sortable: true,
      render: (r) => <span className="pf-muted">{when(r.created_at)}</span> },
    { key: 'type', label: 'Type', sortable: true,
      render: (r) => <span style={{ textTransform: 'capitalize' }}>{r.type}</span> },
    // [2026-09-17] CHANGED — the reason in English, not the machine value.
    // OLD: render: (r) => r.reason || <span className="pf-muted">—</span> },
    // The raw string stays on the row for search (searchIn above) and in the CSV export, so
    // nothing that keys on `released_interview_abandoned` stops working. `title` keeps the exact
    // stored value one hover away — a customer on the phone to support can still read it out.
    { key: 'reason', label: 'Reason', sortable: true,
      render: (r) => (r.reason
        ? <span title={r.reason}>{reasonText(r.reason)}</span>
        : <span className="pf-muted">—</span>) },
    { key: 'amount_credits', label: 'Change', align: 'right', sortable: true,
      render: (r) => {
        const delta = r.amount_credits || 0
        return (
          <span style={{ color: delta >= 0 ? 'var(--pf-good)' : 'var(--pf-bad-text)',
                         fontWeight: 600 }}>
            {delta >= 0 ? '+' : ''}{fmtCredits(delta)}
          </span>
        )
      } },
    { key: 'balance_after_credits', label: 'Balance after', align: 'right', sortable: true,
      render: (r) => fmtCredits(r.balance_after_credits || 0) },
  ]

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="pf-grid tiles">
        {/* [2026-09-25] ADDED — the SAME four tiles as the Dashboard, same labels, same icons, same
            Figma icon colours, same figures. Asked for by Rujal: "I want the same cards that are
            present on the dashboard, the four cards with the same spelling".

            WHY THE NUMBERS ARE GUARANTEED TO MATCH THE DASHBOARD. The Dashboard reads
            `session.credits` from GET /me; this screen reads `summary` from GET /me/credits. Both
            are the one backend call, `backend.credits.summary(client_id)` (proctoring-sdk-backend
            app/modules/me/route.py:311 and :795), so the four fields below are the same values.
            Formatting is copied too — num(..., { decimals: 0 }) and `?? null` so a missing field
            reads "—" rather than a false 0 — so the two screens cannot disagree by a rounding.

            WHAT THE OLD TILES SAID, and where it went. "Balance" was the LEDGER balance, which
            includes credits held by running interviews; "Credits Remaining" is what can be spent
            now (available). The held amount is the "In Progress" tile, so nothing the old
            Balance sub-line said is lost. The old Balance / Granted / Spent tiles are kept below,
            switched off with `false &&`. */}
        <PfTile label="Total Credits" icon={CardIcon} iconTone="blue"
                value={num(s.total_granted_credits ?? null, { decimals: 0 })} />
        <PfTile label="Credits Utilized" icon={TrendingUpIcon} iconTone="teal"
                value={num(s.total_spent_credits ?? null, { decimals: 0 })} />
        <PfTile label="Credits Remaining" icon={ZapIcon} iconTone="red"
                value={num(s.available_credits ?? s.balance_credits ?? null, { decimals: 0 })}
                tone={s.low_balance ? 'var(--pf-bad)' : undefined} />
        <PfTile label="In Progress" icon={CalendarIcon} iconTone="amber"
                value={num(s.held_credits ?? null, { decimals: 0 })}
                sub="interviews running · returned if unused" />

        {/* [2026-09-25] CHANGED — the three tiles below are OFF, replaced by the four above.
            `false &&` rather than a deletion, the same way the Held tile further down was retired:
            the JSX stays live code and still breaks the build if a field is renamed. Delete the
            `false && (<>` and the matching `</>)}` to bring them back. */}
        {false && (<>
        {/* [2026-09-08] CHANGED — the sub-line now explains a held balance.
            OLD: sub={s.low_balance ? 'Low balance' : undefined}

            THIS IS A CONSEQUENCE OF MY OWN EARLIER CHANGE. On 2026-09-07 the "Held" tile was taken
            off this screen because it read 0.00 for almost everyone. That was true then and it is
            not true now: interview billing HOLDS a credit at mint until the video analysis
            completes, so a held balance is the normal state during a run rather than a rarity.

            With the Held tile gone, a customer whose credit is held saw "Balance 1.00" here, and
            then every new interview refused — with nothing on the screen accounting for the
            difference. Removing a tile that is usually zero is reasonable; removing the ONLY
            explanation of a gap is not.

            So the figure stays the ledger balance, which is what "Balance" means and what
            reconciles against the statement below, and the sub-line says what is spendable. Held
            takes priority over the low-balance note because it is the more specific answer to
            "why can I not start an interview" — and the red tone still fires either way. */}
        <PfTile label="Balance" icon={ZapIcon}
                value={fmtCredits(s.balance_credits || 0)} unit="credits"
                tone={s.low_balance ? 'var(--pf-bad)' : undefined}
                sub={s.held_credits
                  ? `${fmtCredits(s.held_credits)} held · `
                    + `${fmtCredits(s.available_credits ?? 0)} available to spend`
                  : (s.low_balance ? 'Low balance' : undefined)} />
        <PfTile label="Granted" icon={CardIcon}
                value={fmtCredits(s.total_granted_credits || 0)} unit="credits" />
        <PfTile label="Spent" icon={TrendingUpIcon}
                value={fmtCredits(s.total_spent_credits || 0)} unit="credits" />
        </>)}
        {/* [2026-09-07] TAKEN OFF — the "Held" tile. Hidden, not deleted.

            It showed credits reserved by runs still in flight, which is a detail of how billing is
            settled rather than a number a customer acts on: it is 0.00 whenever nothing is running,
            and it resolves into Spent by itself moments later. A tile that is almost always zero
            teaches people to ignore that row of the screen.

            `false &&` rather than a comment block so the JSX stays live code — still linted, still
            breaks the build if `held_credits` is renamed. Delete the two words to bring it back.

            The dashboard's "Credits Remaining" tile still surfaces held credits in its sub-line when
            there are any, so the figure is not unreachable — only off this screen. */}
        {false && (
        <PfTile label="Held" icon={CardIcon}
                value={fmtCredits(s.held_credits || 0)} unit="credits"
                sub="reserved by in-flight runs" />
        )}
      </div>

      {/* [2026-09-11] CHANGED — off, with its twin on the Dashboard. Asked for by Rujal.
          OLD: {s.low_balance && (
          See the longer note at the matching block in PlatformDashboard.jsx for why both went
          together and why this is `false &&` rather than a deletion. The Balance tile on this
          screen still renders red on `low_balance`, so the state is still visible.
          [2026-09-25] That tile is now "Credits Remaining" (same red on `low_balance`). */}
      {false && s.low_balance && (
        <PfNotice kind="warn">
          Your balance is low. Credits are added by your InCruiter account manager — contact them to
          top up.
        </PfNotice>
      )}

      {/* [2026-09-06] ADDED — placed ABOVE the spend chart on purpose. The chart says WHEN the
          balance went down; this says WHAT it went down on and whether that work succeeded, which is
          the question someone opening this screen after an unexpected drop is actually asking. */}
      {SHOW_SERVICE_BREAKDOWN && (
      <ServiceBreakdown summary={s} analytics={traffic.data} days={days}
                        loading={traffic.loading}
                        failed={!traffic.loading && !traffic.data} />
      )}

      <PfCard title="Spend over time" subtitle={`Credits consumed per day · last ${days} days`}>
        {/* [2026-09-25] CHANGED — drawn in the Dashboard's navy (#133f7d, the NAVY its Sessions
            chart uses — PlatformDashboard.jsx), not the default green. Asked for by Rujal: "I want
            the graph in blue colour".
            OLD: <CreditsChart data={chart} /> */}
        <CreditsChart data={chart} color="#133f7d" />
      </PfCard>

      {/* [2026-09-05] CHANGED — the ledger was a bare <Table> of up to 200 rows with no search, no
          filter, no sort and no pagination: the one screen a customer opens to reconcile an invoice
          was the one table you could not navigate. It is now the shared DataTable, which also gives
          it Export page / Export all for free — and downloadCsv already neutralises spreadsheet
          formula injection (ui.jsx:318), which matters because `reason` is operator-supplied text.

          The subtitle still states the ledger's own limit, so a reconciliation cannot silently be
          done against a partial file. */}
      <DataTable
        subtitle={`Newest first · showing the ${num(ledger.length)} most recent movements`}
        columns={columns}
        fetchPage={fetchPage}
        pageSize={25}
        filterLabel="Ledger"
        searchPlaceholder="Search type, reason or amount…"
        filters={[
          { key: 'direction', label: 'Direction',
            options: [{ value: 'in', label: 'Added' }, { value: 'out', label: 'Spent' }] },
          ...(types.length > 1
            ? [{ key: 'type', label: 'Type',
                 options: types.map((t) => ({ value: t, label: t })) }]
            : []),
        ]}
        rowKey={(r, i) => r.idempotency_key || `${r.created_at}-${i}`}
        emptyTitle="No movements yet"
        emptyHint="Grants and usage will appear here once your account has activity."
        csvName="credit-ledger"
      />
    </div>
  )
}
